/**
 * Initializes components within the application by observing and processing DOM elements.
 * It relies on ComponentConstructor for dynamic component creation and tracking.
 * @module component_initializer
 */

// Depends on prototype.js // FIXME
import {prototypeJSAjaxRespondersDefined} from "../helpers/prototypejs_helpers";
import PeriodicExecuter from "../helpers/periodic_executer";
import {getThrottledFunction} from "../helpers/throttle";
import {ComponentRegistry} from "./component_registry";
import {constructComponents, getComponentRootNodes} from "./component_constructor";

// We mark an element as initialized by adding the class modifier to the element's classList eg. "foo_component--inited".
// This is generally only used for the purpose of styling the element differently when it is initialized.
const InitializedClassModifier = 'inited';

class ComponentInitializer {

    /**
     * Creates a ComponentInitializer instance.
     * @param {Object} configuration - Configuration object for the component.
     * @param {Object} [options={}] - Additional options.
     */
    constructor(configuration, options = {}) {
        this.configure(configuration);
        this.componentRegistry = options.hasOwnProperty('componentRegistry') ? options['componentRegistry'] : ComponentRegistry.getDefault();
        if (this.componentRegistry) this.componentRegistry.registerTypeKey(this.componentTypeKey);

        this.observeElements();
        this.periodicObserve();
        this.handleResponders();
        this.handlePageUpdate();
    }

    /**
     * Configures the component initializer with specific settings.
     * @param {Object} config - The configuration object.
     */
    configure(config) {
        this.klass = config.klass;
        this.componentTypeKey = config.componentTypeKey;
        this.baseHtmlClass = config.baseHtmlClass;
        this.selector = config.selector;
        this.initializedHtmlClass = config.initializedHtmlClass;
    }

    /**
     * Observes and initializes components based on the current configuration.
     */
    observeElements() {
        const klassInstances = constructComponents(this);
        klassInstances.forEach((klassInstance) => {
            klassInstance.componentInitializer = this;
            klassInstance.componentRegistry = this.componentRegistry;
            this.registerRankersComponent(klassInstance);
            this.markElementAsInitialized(klassInstance.element);
        });
    }

    /**
     * Handles AJAX responses for dynamic initialization of components.
     * Observe the elements after a Prototype.js ajax call completes.
     */
    handleResponders() {
        if (!prototypeJSAjaxRespondersDefined()) return;
        Ajax.Responders.register({
            onComplete: this.observeElements.bind(this)
        });
    }

    /**
     * Sets up observers to handle updates to the DOM, initializing components as needed.
     */
    handlePageUpdate() {
        const rootNodes = getComponentRootNodes();
        if (!rootNodes || rootNodes.length < 1) return;
        const observeElementsHandler = getThrottledFunction(this.observeElements.bind(this), 50);
        this.mutationObserver = new MutationObserver(observeElementsHandler);
        rootNodes.forEach((rootNode) => {
            this.mutationObserver.observe(rootNode, {childList: true, subtree: true});
        });
    }

    /**
     * Periodically checks and initializes components.
     */
    periodicObserve() {
        this.timer = new PeriodicExecuter(function () {
            this.observeElements();
        }.bind(this), 1);
    }

    /**
     * Marks a DOM element as initialized with a specific component class.
     * @param {Element} el - The DOM element to mark as initialized.
     */
    markElementAsInitialized(el) {
        el.classList.add(this.initializedHtmlClass);
    }

    /**
     * Registers a component instance in the component registry.
     * @param {Object} klassInstance - The component instance to register.
     */
    registerRankersComponent(klassInstance) {
        if (!this.componentTypeKey || !this.componentRegistry) return;
        this.componentRegistry.pushComponent(this.componentTypeKey, klassInstance);
    }

}

/**
 * Function to create and initialize a ComponentInitializer instance.
 * @param {Object} configuration - Configuration object for the component.
 * @param {Object} [options={}] - Additional options.
 */
export const componentLoader = function (configuration, options = {}) {
    new ComponentInitializer(configuration, options);
};

/**
 * Generates a configuration object for a component.
 * @param {Function} componentClass - The class of the component.
 * @param {string} componentTypeKey - The key identifying the type of component.
 * @param {string} baseHtmlClass - The base HTML class for the component.
 * @returns {Object} Configuration object for the component.
 */
export const componentConfig = function (componentClass, componentTypeKey, baseHtmlClass) {
    return {
        klass: componentClass,
        componentTypeKey: componentTypeKey,
        baseHtmlClass: baseHtmlClass,
        initializedHtmlClass: `${baseHtmlClass}--${InitializedClassModifier}`,
        selector: `.${baseHtmlClass}`,
        notInitializedSelector: `.${baseHtmlClass}:not(.${baseHtmlClass}--${InitializedClassModifier})`,
    }
};
